import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';

// Import calendar only on native platforms to avoid build errors
let Calendar = null;
if (Capacitor.isNativePlatform()) {
  try {
    Calendar = require('@ebarooni/capacitor-calendar').Calendar;
  } catch (error) {
    console.warn('📅 Calendar plugin not available:', error);
  }
}

class CalendarService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.isInitialized = false;
    this.deviceInfo = null;
    this.defaultCalendar = null;
    this.devimanaCalendar = null;
    this.init();
  }

  async init() {
    if (this.isNative && Calendar) {
      try {
        this.deviceInfo = await Device.getInfo();
        await this.setupCalendarPermissions();
        await this.setupDevimanaCalendar();
        this.isInitialized = true;
        console.log('📅 CalendarService initialized successfully');
      } catch (error) {
        console.error('📅 CalendarService initialization error:', error);
      }
    } else {
      console.log('📅 Calendar service not available on this platform');
    }
  }

  // Request calendar permissions
  async setupCalendarPermissions() {
    if (!Calendar) return false;
    
    try {
      // Try different permission methods based on plugin version
      let permissions;
      if (typeof Calendar.requestPermissions === 'function') {
        permissions = await Calendar.requestPermissions();
      } else if (typeof Calendar.requestWritePermissions === 'function') {
        permissions = await Calendar.requestWritePermissions();
      } else {
        console.log('📅 Permission method not found, assuming granted');
        return true;
      }
      
      console.log('📅 Calendar permissions:', permissions);
      
      if (permissions && (permissions.result === 'granted' || permissions === true)) {
        console.log('✅ Calendar permissions granted');
        return true;
      } else {
        console.warn('❌ Calendar permissions denied');
        return false;
      }
    } catch (error) {
      console.error('📅 Calendar permissions error:', error);
      return false;
    }
  }

  // Setup or find Devimana Calendar
  async setupDevimanaCalendar() {
    if (!Calendar) return;
    
    try {
      // Try to get calendars list first
      let calendars = [];
      if (typeof Calendar.listCalendars === 'function') {
        const calendarsResult = await Calendar.listCalendars();
        calendars = calendarsResult.result || calendarsResult || [];
      } else if (typeof Calendar.getCalendars === 'function') {
        calendars = await Calendar.getCalendars();
      }
      
      console.log('📅 Available calendars:', calendars.length);
      
      // Look for existing Devimana calendar
      this.devimanaCalendar = calendars.find(cal => 
        cal.title === 'Devimana Events' || 
        cal.displayName === 'Devimana Events' ||
        cal.name === 'Devimana Events'
      );
      
      // Use first available calendar as default
      this.defaultCalendar = calendars[0] || { id: 'default', title: 'Default Calendar' };
      
      // If no Devimana calendar exists, we'll use the default for now
      if (!this.devimanaCalendar) {
        console.log('📅 No Devimana calendar found, will use default calendar');
        this.devimanaCalendar = this.defaultCalendar;
      } else {
        console.log('✅ Found existing Devimana calendar:', this.devimanaCalendar);
      }
      
    } catch (error) {
      console.error('📅 Setup calendar error:', error);
      // Create a basic fallback
      this.defaultCalendar = { id: 'default', title: 'Default Calendar' };
      this.devimanaCalendar = this.defaultCalendar;
    }
  }

  // Add event to device calendar
  async addEventToCalendar(eventData) {
    if (!this.isNative || !this.isInitialized || !Calendar) {
      console.log('📅 Calendar service not available (web platform or plugin not loaded)');
      return { success: false, error: 'Calendar not available on this platform' };
    }

    try {
      // Prepare event data for calendar
      const calendarEvent = this.prepareCalendarEvent(eventData);
      
      // Use Devimana calendar or fallback to default
      const targetCalendar = this.devimanaCalendar || this.defaultCalendar;
      
      if (!targetCalendar) {
        throw new Error('No calendar available');
      }

      console.log('📅 Creating calendar event:', calendarEvent);

      // Try to create event with different method signatures
      let createResult;
      if (typeof Calendar.createEvent === 'function') {
        createResult = await Calendar.createEvent({
          calendarId: targetCalendar.id,
          ...calendarEvent
        });
      } else if (typeof Calendar.addEvent === 'function') {
        createResult = await Calendar.addEvent(calendarEvent);
      } else {
        throw new Error('No create event method available');
      }

      // Handle different result formats
      const eventId = createResult?.result?.id || createResult?.id || 'created';
      
      if (createResult && (createResult.result || createResult.id || createResult === true)) {
        console.log('✅ Event added to calendar:', createResult);
        return { 
          success: true, 
          eventId: eventId,
          calendarId: targetCalendar.id,
          calendarName: targetCalendar.title || targetCalendar.displayName || targetCalendar.name
        };
      } else {
        throw new Error('Failed to create calendar event');
      }
      
    } catch (error) {
      console.error('📅 Add event to calendar error:', error);
      return { success: false, error: error.message };
    }
  }

  // Prepare event data for calendar API
  prepareCalendarEvent(eventData) {
    let startDate, endDate;
    
    if (eventData.calendar_type === 'gregorian') {
      // For Gregorian events, use the selected date
      startDate = new Date(eventData.gregorian_date);
      endDate = new Date(startDate);
      endDate.setHours(startDate.getHours() + 1); // Default 1-hour duration
      
    } else {
      // For Devimana events, calculate Gregorian equivalent
      // This is a simplified approach - you might want to implement
      // proper Devimana to Gregorian conversion
      startDate = new Date(); // For now, use current date
      endDate = new Date(startDate);
      endDate.setHours(startDate.getHours() + 1);
    }

    // Prepare description with Tithi information
    let description = eventData.description || '';
    
    if (eventData.tithi_info) {
      description += `\n\n🌙 Tithi Information:`;
      if (eventData.tithi_info.tithi_name) {
        description += `\n• Tithi: ${eventData.tithi_info.tithi_name}`;
      }
      if (eventData.tithi_info.paksha) {
        description += `\n• Paksha: ${eventData.tithi_info.paksha}`;
      }
      if (eventData.tithi_info.tithi_progress) {
        description += `\n• Progress: ${(eventData.tithi_info.tithi_progress * 100).toFixed(1)}%`;
      }
    }

    if (eventData.calendar_type === 'devimana') {
      description += `\n\n📅 Devimana Date:`;
      description += `\n• Yuga: ${eventData.yuga}`;
      description += `\n• Varsha: ${eventData.varsha}`;
      description += `\n• Masa: ${eventData.masa}`;
      description += `\n• Divasa: ${eventData.divasa}`;
      if (eventData.ghatika) {
        description += `\n• Ghatika: ${eventData.ghatika}`;
      }
    }

    description += `\n\n🕉️ Created with Devimana Calendar App`;

    return {
      title: eventData.title,
      notes: description,
      startDate: startDate.getTime(), // Unix timestamp in milliseconds
      endDate: endDate.getTime(),
      isAllDay: false,
      location: eventData.location || '',
      url: '', // Could add app deep link
      // Set reminder for 24 hours before (1440 minutes)
      alarms: [
        {
          alarmOffset: 1440 // 24 hours in minutes
        }
      ]
    };
  }

  // Remove event from calendar
  async removeEventFromCalendar(calendarEventId, calendarId) {
    if (!this.isNative || !this.isInitialized) {
      return { success: false, error: 'Not on native platform' };
    }

    try {
      await Calendar.deleteEvent({
        calendarId: calendarId,
        eventId: calendarEventId
      });
      
      console.log('✅ Event removed from calendar');
      return { success: true };
      
    } catch (error) {
      console.error('📅 Remove event from calendar error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get all calendars
  async getCalendars() {
    if (!this.isNative || !this.isInitialized) {
      return { success: false, calendars: [] };
    }

    try {
      const result = await Calendar.listCalendars();
      return { success: true, calendars: result.result };
    } catch (error) {
      console.error('📅 Get calendars error:', error);
      return { success: false, calendars: [] };
    }
  }

  // Check if calendar integration is available
  isSupported() {
    return this.isNative && Calendar !== null;
  }

  // Get device info
  getDeviceInfo() {
    return this.deviceInfo;
  }

  // Get calendar names for user display
  getCalendarInfo() {
    if (!this.isInitialized) return null;
    
    return {
      defaultCalendar: this.defaultCalendar?.title || this.defaultCalendar?.displayName,
      devimanaCalendar: this.devimanaCalendar?.title || this.devimanaCalendar?.displayName,
      isDevimanaCalendarCreated: this.devimanaCalendar?.title === 'Devimana Events'
    };
  }
}

export default new CalendarService();